import React, { useState, useEffect, useContext, useRef } from "react";
import { X } from "lucide-react";
import { Rnd } from "react-rnd";
import { GiResize } from "react-icons/gi";
import {
  CanvasScaleContext,
  CanvasMoveModeContext,
} from "../components/Form/Canvas/ScaleContext";

const CONTAINER_WIDTH = 800;
const CONTAINER_HEIGHT = 600;

const BaseDraggable = ({
  id,
  type,
  position,
  children,
  className = "",
  onDelete,
  style = {},
  width,
  height,
  onResize,
  minWidth = 200,
  minHeight = 120,
  maxBottom,
  onDragStart,
  onDragStop: customDragStop,
  disableDragging = false,
  scale,
  deleteButtonPosition = "-top-2 -right-2",
}) => {
  const scaleFromContext = useContext(CanvasScaleContext);
  const moveMode = useContext(CanvasMoveModeContext);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [dragEnabled, setDragEnabled] = useState(true);
  const [showHandles, setShowHandles] = useState(false);
  const longPressTimerRef = useRef(null);
  const handlesHideTimerRef = useRef(null);
  const touchMovedRef = useRef(false);
  const touchStartPosRef = useRef({ x: 0, y: 0 });
  const componentRef = useRef(null);
  const [dimensions, setDimensions] = useState({
    width: width || style.width || 320,
    height: height || style.height || 160,
  });

  const [pos, setPos] = useState({
    x: position.left || 0,
    y: position.top || 0,
  });

  useEffect(() => {
    // Detect touch devices
    const touchCapable =
      typeof window !== "undefined" &&
      ("ontouchstart" in window || navigator.maxTouchPoints > 0);
    setIsTouchDevice(!!touchCapable);
    setDragEnabled(!touchCapable);
  }, []);

  useEffect(() => {
    return () => {
      if (handlesHideTimerRef.current) {
        clearTimeout(handlesHideTimerRef.current);
        handlesHideTimerRef.current = null;
      }
    };
  }, []);

  // Announce that this component has shown handles, so others can hide theirs
  const announceHandlesShown = () => {
    try {
      window.dispatchEvent(
        new CustomEvent("componentHandlesShown", { detail: { id } })
      );
    } catch {}
  };

  const revealHandlesTemporarily = (durationMs = 1200) => {
    setShowHandles(true);
    announceHandlesShown();
    if (handlesHideTimerRef.current) {
      clearTimeout(handlesHideTimerRef.current);
    }
    handlesHideTimerRef.current = setTimeout(() => {
      setShowHandles(false);
    }, durationMs);
  };

  // Hide handles when another component shows its handles
  useEffect(() => {
    const handleExternalShown = (e) => {
      if (!e || !e.detail || e.detail.id === id) return;
      if (handlesHideTimerRef.current) {
        clearTimeout(handlesHideTimerRef.current);
        handlesHideTimerRef.current = null;
      }
      setShowHandles(false);
    };
    window.addEventListener("componentHandlesShown", handleExternalShown);
    return () => {
      window.removeEventListener("componentHandlesShown", handleExternalShown);
    };
  }, [id]);

  // Click/tap outside hides handles
  useEffect(() => {
    const hideIfClickedOutside = (e) => {
      if (!componentRef.current) return;
      if (!componentRef.current.contains(e.target)) {
        if (handlesHideTimerRef.current) {
          clearTimeout(handlesHideTimerRef.current);
          handlesHideTimerRef.current = null;
        }
        setShowHandles(false);
      }
    };
    document.addEventListener("mousedown", hideIfClickedOutside);
    document.addEventListener("touchstart", hideIfClickedOutside);
    return () => {
      document.removeEventListener("mousedown", hideIfClickedOutside);
      document.removeEventListener("touchstart", hideIfClickedOutside);
    };
  }, []);

  useEffect(() => {
    setDimensions({
      width: width || style.width || 320,
      height: height || style.height || 160,
    });
  }, [width, height, style.width, style.height]);

  useEffect(() => {
    setPos({
      x: position.left || 0,
      y: position.top || 0,
    });
  }, [position.left, position.top]);

  const allowedBottom =
    typeof maxBottom !== "undefined" ? maxBottom : CONTAINER_HEIGHT;
  const maxHeight = allowedBottom - (position.top || 0);

  const handleDragStart = (e, d) => {
    if (onDragStart) {
      onDragStart(e, d);
    }
    setShowHandles(true);
    announceHandlesShown();
    if (handlesHideTimerRef.current) {
      clearTimeout(handlesHideTimerRef.current);
      handlesHideTimerRef.current = null;
    }
  };

  const handleDragStop = (e, d) => {
    const newX = Math.max(0, Math.min(d.x, CONTAINER_WIDTH - dimensions.width));
    const newY = Math.max(
      0,
      Math.min(d.y, CONTAINER_HEIGHT - dimensions.height)
    );

    setPos({ x: newX, y: newY });

    if (customDragStop) {
      customDragStop(e, { x: newX, y: newY }); // Pass clamped coordinates
    } else {
      window.dispatchEvent(
        new CustomEvent("componentDropped", {
          detail: {
            id,
            type,
            x: newX,
            y: newY,
          },
        })
      );
    }
    // After drag on touch, disable dragging again until next long-press
    if (isTouchDevice) {
      setDragEnabled(false);
    }
    // Hide handles shortly after drag completes
    revealHandlesTemporarily(1200);
  };

  const handleResizeStop = (e, direction, ref, delta, position) => {
    let newDimensions = {
      width: dimensions.width + delta.width,
      height: dimensions.height + delta.height,
    };

    newDimensions.width = Math.min(
      newDimensions.width,
      CONTAINER_WIDTH - pos.x
    );
    newDimensions.height = Math.min(newDimensions.height, maxHeight);

    setDimensions(newDimensions);
    if (onResize) {
      onResize(id, newDimensions);
    }
  };

  //   const handleResizeStop = (e, direction, ref, delta, position) => {
  //   const maxDelta = Math.max(delta.width, delta.height);
  //   console.log("Max Delta:", maxDelta);

  //   let newWidth = dimensions.width + maxDelta;
  //   let newHeight = dimensions.height + maxDelta;

  //   newWidth = Math.min(newWidth, CONTAINER_WIDTH - pos.x);
  //   newHeight = Math.min(newHeight, allowedBottom - pos.y);

  //   const newDimensions = {
  //     width: newWidth,
  //     height: newHeight,
  //   };

  //   setDimensions(newDimensions);

  //   if (onResize) {
  //     onResize(id, newDimensions);
  //   }
  // };

  const handleTouchStart = (e) => {
    if (!isTouchDevice) return;
    if (e && e.touches && e.touches[0]) {
      touchStartPosRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    }
    touchMovedRef.current = false;
    setDragEnabled(false);
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      setDragEnabled(true);
      setShowHandles(true);
    }, 300);
    // Avoid calling preventDefault here to respect passive listeners
    // Briefly show handles on touch start
    revealHandlesTemporarily(1500);
  };

  const handleTouchMove = (e) => {
    if (!isTouchDevice) return;
    touchMovedRef.current = true;
    if (!dragEnabled) {
      // Cancel long-press if user moves finger before timer
      if (e && e.touches && e.touches[0]) {
        const dx = e.touches[0].clientX - touchStartPosRef.current.x;
        const dy = e.touches[0].clientY - touchStartPosRef.current.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > 12 && longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (!isTouchDevice) return;
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    // Briefly show handles after touch ends
    revealHandlesTemporarily(1200);
    // Do not call preventDefault to avoid passive listener warnings
  };

  const handleMouseDown = () => {
    // Enable dragging immediately for mouse interactions
    if (!isTouchDevice) {
      setDragEnabled(true);
      // Show handles briefly on desktop interaction
      revealHandlesTemporarily(1200);
    }
  };

  return (
    <Rnd
      size={{ width: dimensions.width, height: dimensions.height }}
      position={{ x: pos.x, y: pos.y }}
      scale={scale ?? scaleFromContext ?? 1}
      onDragStart={handleDragStart}
      onDragStop={handleDragStop}
      onResizeStop={handleResizeStop}
      minWidth={minWidth}
      minHeight={minHeight}
      maxHeight={maxHeight}
      disableDragging={moveMode ? false : disableDragging || !dragEnabled}
      enableResizing={{
        bottomRight: true,
      }}
      cancel={moveMode ? "" : "input, label, [data-no-drag]"} // Prevent dragging on interactive elements unless in move mode
      resizeHandleStyles={{
        bottomRight: { cursor: "se-resize" },
      }}
      resizeHandleComponent={{
        bottomRight: (
          // <div className="w-3 h-3 bg-transparent hover:bg-blue-200 transition-colors" />
          <div
            className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity transform rotate-90  flex items-center justify-center"
            style={{ opacity: showHandles ? 1 : undefined }}
          >
            <GiResize className="text-gray-500" />
          </div>
        ),
      }}
      bounds="parent"
      className={`absolute rounded-lg shadow-lg border-2 border-gray-300 bg-white
        hover:shadow-xl group ${className}`}
      style={{
        opacity: 1,
        transition: "box-shadow 0.2s",
        ...style,
      }}
    >
      {moveMode && (
        <div
          className="pointer-events-auto"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 5,
            cursor: "move",
            background: "transparent",
          }}
        />
      )}
      <div
        className="w-full h-full cursor-grab active:cursor-grabbing"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        onMouseDown={handleMouseDown}
        style={{
          touchAction: "none",
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitTouchCallout: "none",
        }}
        ref={componentRef}
      >
        {onDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className={`absolute ${deleteButtonPosition} p-1.5 bg-red-500 text-white
              rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-600
              transition-opacity z-10 shadow-lg`}
            style={{ opacity: showHandles ? 1 : undefined }}
          >
            <X size={14} />
          </button>
        )}
        {children}
      </div>
    </Rnd>
  );
};

export default BaseDraggable;
